import { useEffect, useRef, useState } from "react";
import { recorderMime, voiceCeilingSeconds } from "../lib/messages";

/* THE VOICE ENGINE — `useVoiceRecording`, the ONE voice recorder of the web.
 *
 * ⚠️ AAC IN MP4 OR NOTHING (`recorderMime`): the phone cannot play WebM, and the database refuses any path but
 * `.m4a`. A browser that can only record WebM gets no mic — never a note that arrives silent. (Chrome records
 * `audio/mp4` since 126; Safari always has.)
 * ⚠️ NOTHING IS UPLOADED HERE. The recording is a Blob in this tab until Send.
 * JOB 3 STEP 5 PAGE 5 (2026-10-09): W72 step 6's hold-to-record mic and its preview row (`VoiceRecorder`) are GONE — the
 * Inbox thread's reply is the ONE composer (`ReviewComposer.tsx`), whose mic (click, or hold while held) records through
 * this hook via `useReviewRecorder`, the take playing in the field before Send.
 *
 * 🔴 W82 STEP 5 — THE ONE VOICE RECORDER: the recording engine is `useVoiceRecording` below, and the review's Voice
 * container (`ReviewMedia.tsx`) records through THIS hook — never a second `getUserMedia({ audio })` + `MediaRecorder`
 * (the gate `w82-5-the-media` counts the definitions).
 */

/** A finished recording: its Blob (AAC in MP4), its whole seconds (1..the ceiling), and the tag its `start` was given. */
export type VoiceTake = { blob: Blob; seconds: number; tag: string };

export type VoiceRecording = {
  /** The container this browser records in, or null (Firefox, old Chrome): no mic is offered then. */
  mime: string | null;
  recording: boolean;
  elapsed: number;
  levels: number[];
  error: string | null;
  /** Ask for the mic and start. Resolves false when refused, unsupported, or released before the permission came back.
   *  `tag` comes back on the take (the review's slot); `ceilingSeconds` overrides the hook's for this recording. */
  start: (opts?: { tag?: string; ceilingSeconds?: number }) => Promise<boolean>;
  /** Stop (the take arrives through `onTake`), or — if the permission prompt is still open — cancel the start. */
  release: () => void;
};

/** ONE recording's own resources. ⚠️ PER SESSION, NEVER SHARED REFS: the review stops one recording and starts the next in
 *  the same tick (stop-on-next), and the stopped one's `onstop` fires LATER — with shared refs it would build its Blob from
 *  the new recording's chunks and tear down the new recording's microphone. */
type Session = {
  rec: MediaRecorder; media: MediaStream; chunks: Blob[]; startedAt: number; tag: string; ceilingSeconds: number;
  raf: number | null; timer: number | null; ctx: AudioContext | null;
};

export function useVoiceRecording({ onTake, ceilingSeconds = voiceCeilingSeconds, disabled = false }: {
  onTake: (take: VoiceTake) => void; ceilingSeconds?: number; disabled?: boolean;
}): VoiceRecording {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const current = useRef<Session | null>(null);
  const pending = useRef(false);
  const takeTo = useRef(onTake);
  takeTo.current = onTake;

  const mime = typeof MediaRecorder === "undefined" ? null
    : recorderMime((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } });

  useEffect(() => () => {
    pending.current = false;
    const s = current.current;
    current.current = null;
    if (s !== null) { s.rec.onstop = null; if (s.rec.state !== "inactive") s.rec.stop(); teardown(s); }
  }, []);

  function teardown(s: Session) {
    if (s.raf !== null) cancelAnimationFrame(s.raf);
    s.raf = null;
    if (s.timer !== null) window.clearTimeout(s.timer);
    s.timer = null;
    s.media.getTracks().forEach((tr) => tr.stop());
    void s.ctx?.close().catch(() => undefined);
    s.ctx = null;
  }

  async function start(opts?: { tag?: string; ceilingSeconds?: number }): Promise<boolean> {
    if (mime === null || disabled || current.current !== null || pending.current) return false;
    pending.current = true;
    setError(null);
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      pending.current = false;
      setError("The microphone was not allowed. Your browser's site settings can allow it.");
      return false;
    }
    // Released before the permission prompt came back: nothing to record.
    if (!pending.current) { media.getTracks().forEach((tr) => tr.stop()); return false; }
    pending.current = false;
    const rec = new MediaRecorder(media, { mimeType: mime });
    const s: Session = {
      rec, media, chunks: [], startedAt: performance.now(), tag: opts?.tag ?? "", ceilingSeconds: opts?.ceilingSeconds ?? ceilingSeconds,
      raf: null, timer: null, ctx: null,
    };
    rec.ondataavailable = (e) => { if (e.data.size > 0) s.chunks.push(e.data); };
    rec.onstop = () => {
      const seconds = Math.min(s.ceilingSeconds, Math.max(1, Math.round((performance.now() - s.startedAt) / 1000)));
      teardown(s);
      if (current.current === s) current.current = null;
      if (current.current === null) setRecording(false);
      const blob = new Blob(s.chunks, { type: "audio/mp4" });
      if (blob.size > 0) takeTo.current({ blob, seconds, tag: s.tag });
    };
    current.current = s;
    rec.start(250);
    setRecording(true);
    setElapsed(0);
    setLevels([]);

    // The live waveform and the timer, from one animation loop — drawn only while THIS session is the current one.
    const ctx = new AudioContext();
    s.ctx = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(media).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (current.current !== s) return;
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v - 128) / 128);
      setLevels((prev) => [...prev.slice(-39), peak]);
      setElapsed((performance.now() - s.startedAt) / 1000);
      s.raf = requestAnimationFrame(tick);
    };
    s.raf = requestAnimationFrame(tick);
    // THE CEILING — the recorder stops itself so the database never has to refuse a note.
    s.timer = window.setTimeout(() => { if (current.current === s) release(); }, s.ceilingSeconds * 1000);
    return true;
  }

  function release() {
    pending.current = false;
    const s = current.current;
    current.current = null;
    if (s !== null && s.rec.state !== "inactive") s.rec.stop();
  }

  return { mime, recording, elapsed, levels, error, start, release };
}
